import { observable, set } from 'mobx';
import { createContext, PropsWithChildren, ReactElement } from 'react';
import { pageSizeOptions } from '../../design/pagination/paginationPageSize/S_PaginationPageSizeOptions';

export interface ISettingsStore {
  attachmentsPageSize: number;
  datasetDetailsImagesPageSize: number;
  datasetDetailsProjectsPageSize: number;
  datasetsPageSize: number;
  usersPageSize: number;
  activityLogPageSize: number;
  projectDetailsImagesPageSize: number;
  statisticsPageSize: number;
  projectsPageSize: number;
  projectsCardsPageSize: number;
  teamPageSize: number;
  workspacesPageSize: number;
  projectsExportsPageSize: number;
  adminPanelPageSize: number;
  jobsPageSize: number;
  modelsImagesPageSize: number;
  cloudCostsHistoryPageSize: number;
  adminWorkspacesPageSize: number;
}

interface ISettingsContext {
  store: ISettingsStore;
  setAttachmentsPageSize: (pageSize: number) => void;
  setDatasetDetailsImagesPageSize: (pageSize: number) => void;
  setDatasetDetailsProjectsPageSize: (pageSize: number) => void;
  setDatasetsPageSize: (pageSize: number) => void;
  setUsersPageSize: (pageSize: number) => void;
  setActivityLogPageSize: (pageSize: number) => void;
  setProjectDetailsImagesPageSize: (pageSize: number) => void;
  setStatisticsPageSize: (pageSize: number) => void;
  setProjectsPageSize: (pageSize: number) => void;
  setProjectsCardsPageSize: (pageSize: number) => void;
  setTeamPageSize: (pageSize: number) => void;
  setWorkspacesPageSize: (pageSize: number) => void;
  setProjectsExportsPageSize: (pageSize: number) => void;
  setAdminPanelPageSize: (pageSize: number) => void;
  setJobsPageSize: (pageSize: number) => void;
  setModelsImagesPageSize: (pageSize: number) => void;
  setCloudCostsHistoryPageSize: (pageSize: number) => void;
  setAdminWorkspacesPageSize: (pageSize: number) => void;
}

const settingsContext = (): ISettingsContext => {
  const defaultPageSize = pageSizeOptions[0];
  const SETTINGS_KEY = 'settings';
  const settingsString = localStorage.getItem(SETTINGS_KEY);
  const settings = settingsString ? JSON.parse(settingsString) : {};

  const initialStore = observable({
    attachmentsPageSize: defaultPageSize,
    datasetDetailsImagesPageSize: defaultPageSize,
    datasetDetailsProjectsPageSize: defaultPageSize,
    datasetsPageSize: defaultPageSize,
    usersPageSize: defaultPageSize,
    activityLogPageSize: defaultPageSize,
    projectDetailsImagesPageSize: defaultPageSize,
    statisticsPageSize: defaultPageSize,
    projectsPageSize: defaultPageSize,
    projectsCardsPageSize: defaultPageSize,
    teamPageSize: defaultPageSize,
    workspacesPageSize: defaultPageSize,
    projectsExportsPageSize: defaultPageSize,
    adminPanelPageSize: defaultPageSize,
    modelsPageSize: defaultPageSize,
    jobsPageSize: defaultPageSize,
    cloudCostsHistoryPageSize: defaultPageSize,
    adminWorkspacesPageSize: defaultPageSize,
    ...settings,
  });

  const storeValue = (settingName: string, settingValue: unknown): void => {
    set(initialStore, { [settingName]: settingValue });
    const newSettingsString = localStorage.getItem(SETTINGS_KEY);
    const newSettings = newSettingsString ? JSON.parse(newSettingsString) : {};

    settings[settingName] = settingValue;
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(newSettings));
  };

  const setPageSize =
    (key: keyof ISettingsStore) =>
    (pageSize: number): void => {
      set(initialStore, { [key]: pageSize });
      storeValue(key, pageSize);
    };

  return {
    store: initialStore,
    setAttachmentsPageSize: setPageSize('attachmentsPageSize'),
    setDatasetDetailsImagesPageSize: setPageSize('datasetDetailsImagesPageSize'),
    setDatasetDetailsProjectsPageSize: setPageSize('datasetDetailsProjectsPageSize'),
    setDatasetsPageSize: setPageSize('datasetsPageSize'),
    setUsersPageSize: setPageSize('usersPageSize'),
    setActivityLogPageSize: setPageSize('activityLogPageSize'),
    setProjectDetailsImagesPageSize: setPageSize('projectDetailsImagesPageSize'),
    setStatisticsPageSize: setPageSize('statisticsPageSize'),
    setProjectsPageSize: setPageSize('projectsPageSize'),
    setProjectsCardsPageSize: setPageSize('projectsCardsPageSize'),
    setTeamPageSize: setPageSize('teamPageSize'),
    setWorkspacesPageSize: setPageSize('workspacesPageSize'),
    setProjectsExportsPageSize: setPageSize('projectsExportsPageSize'),
    setAdminPanelPageSize: setPageSize('adminPanelPageSize'),
    setJobsPageSize: setPageSize('jobsPageSize'),
    setModelsImagesPageSize: setPageSize('modelsImagesPageSize'),
    setCloudCostsHistoryPageSize: setPageSize('cloudCostsHistoryPageSize'),
    setAdminWorkspacesPageSize: setPageSize('adminWorkspacesPageSize'),
  };
};

export const Settings = createContext<ISettingsContext>(undefined);

export const SettingsContextProvider = ({ children }: PropsWithChildren): ReactElement => {
  return <Settings.Provider value={settingsContext()}>{children}</Settings.Provider>;
};
